import { FC } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Success2Graphic } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, Modal, ModalControlModalProps, Text } from '@frontend/design-system';
import { getFeatureWiseTrackingId, SelfUpgradeModalTrackingIds } from '../../../constants/tracking-ids';

interface SelfUpgradeSuccessModalProps extends ModalControlModalProps {
  featureEnum: Feature;
  featureName: string;
  onboardingCallUrl: string;
}

export const SelfUpgradeSuccessModal: FC<SelfUpgradeSuccessModalProps> = ({
  featureEnum,
  featureName,
  onboardingCallUrl,
  ...modalProps
}) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'self-upgrade-success-modal' });

  const handleScheduleOnboardingCall = () => {
    window.open(onboardingCallUrl, '_blank', 'noopener noreferrer');
  };

  return (
    <Modal {...modalProps} maxWidth={600}>
      <Modal.Header
        onClose={modalProps.onClose}
        closeBtnTrackingId={getFeatureWiseTrackingId(featureEnum, SelfUpgradeModalTrackingIds.successModalCloseAction)}
      />
      <Modal.Body
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(2),
        }}
      >
        <Text textAlign='center'>
          {t(
            'You are successfully subscribed to {{featureName}}. If you need additional assistance, please schedule an optional onboarding call by clicking Schedule Call below.',
            { featureName }
          )}
        </Text>
        <Success2Graphic />
        <Heading textAlign='center' level={2}>
          {t('You are subscribed!')}
        </Heading>
        <article css={bottomContentStyles}>
          <div css={{ marginRight: 'auto' }}>
            <Text size='large' weight='bold'>
              {t('Optional Onboarding Call - 45 min')}
            </Text>
            <Text textAlign='left' size='medium' color='light'>
              {t('with your onboarding specialist')}
            </Text>
          </div>
          <Button
            variant='secondary'
            iconName='calendar'
            onClick={handleScheduleOnboardingCall}
            trackingId={getFeatureWiseTrackingId(featureEnum, SelfUpgradeModalTrackingIds.onboardingCallAction)}
          >
            {t('Schedule Call')}
          </Button>
        </article>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Okay'),
          trackingId: getFeatureWiseTrackingId(featureEnum, SelfUpgradeModalTrackingIds.successModalOkayAction),
          onClick: modalProps.onClose,
        }}
      />
    </Modal>
  );
};

const bottomContentStyles = css({
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  padding: theme.spacing(2),
  marginTop: theme.spacing(3),
  display: 'flex',
  gap: theme.spacing(2),
  width: '100%',
  flexWrap: 'wrap',
});
